import type { ReactNode } from "react";

import type { components } from "@/api/types";
import { toChartNumber } from "@/shared/lib";

type ChartPoint = components["schemas"]["ChartPoint"];

/**
 * Две метрики профиля: роботы и заработок.
 *
 * Каждая карточка несёт собственную декорацию, и обе — CSS/SVG, без единого
 * растрового куска. У «Роботов» это перспективный пол и контур робота, у
 * «Заработано» — мини-график.
 *
 * График рисуется по НАСТОЯЩЕМУ ряду (`spark` из `/profile`), а не по
 * декоративному пути из макета: линия на денежной карточке, не связанная с
 * деньгами, — это ложь, и заметят её ровно тогда, когда она пойдёт вверх на
 * падающем счёте.
 */
interface ProfileMetricsProps {
  robotsLabel: string;
  robotsValue: string;
  earnedLabel: string;
  earnedValue: ReactNode;
  spark: ChartPoint[];
}

export function ProfileMetrics({
  robotsLabel,
  robotsValue,
  earnedLabel,
  earnedValue,
  spark,
}: ProfileMetricsProps) {
  return (
    <dl className="grid grid-cols-2 gap-2">
      <MetricCard tone="cyan" label={robotsLabel} value={robotsValue}>
        {/* Пол в перспективе: две сетки линий, наклонённые трансформом.
            Именно наклон, а не готовая картинка, — иначе карточка не смогла бы
            менять ширину вместе с экраном. */}
        <span
          aria-hidden="true"
          className={[
            "absolute -inset-x-2.5 -bottom-3 h-14 origin-bottom opacity-[0.16]",
            "[transform:perspective(90px)_rotateX(55deg)]",
            "bg-[linear-gradient(currentColor_1px,transparent_1px),linear-gradient(90deg,currentColor_1px,transparent_1px)]",
            "bg-[size:14px_10px]",
          ].join(" ")}
        />
        <span
          aria-hidden="true"
          className="absolute top-2.5 right-5 h-7 w-[34px] rounded-[9px] border-2 border-current opacity-10"
        >
          <span className="absolute top-3 left-2 size-[3px] rounded-full bg-current" />
          <span className="absolute top-3 right-2 size-[3px] rounded-full bg-current" />
        </span>
      </MetricCard>

      <MetricCard tone="green" label={earnedLabel} value={earnedValue}>
        {/* Ниже и уже, чем в макете: там сумма «+0,00 USDT» короткая, а
            реальная четырёхзначная налезает на линию и обе становятся
            нечитаемыми. График остаётся фоном, а не спорит с числом. */}
        <span aria-hidden="true" className="absolute -right-1 -bottom-1 h-12 w-[70px] opacity-25">
          <SparkPath points={spark} />
        </span>
      </MetricCard>
    </dl>
  );
}

const TONES = {
  cyan: "border-status-info/50 text-status-info",
  green: "border-status-profit/40 text-status-profit",
} as const;

function MetricCard({
  tone,
  label,
  value,
  children,
}: {
  tone: keyof typeof TONES;
  label: string;
  value: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={[
        "relative flex min-h-[81px] flex-col justify-between overflow-hidden rounded-lg border px-3.5 py-4",
        "bg-[image:var(--surface-panel)]",
        TONES[tone],
      ].join(" ")}
    >
      {children}
      <dt className="relative text-small font-normal text-text-secondary">{label}</dt>
      <dd className="relative mt-2 text-h2 font-semibold tracking-tight text-text-bright">
        {value}
      </dd>
    </div>
  );
}

/**
 * Мини-график: ломаная по ряду, кружки на каждой второй точке и вертикальные
 * выноски к основанию.
 *
 * Собственный SVG, а не Recharts: карточка 94×60 пикселей, а `ResponsiveContainer`
 * тянет за собой измерение размеров и целый чанк библиотеки ради семи точек.
 * Прямой путь дешевле и не мигает на первом кадре.
 */
function SparkPath({ points }: { points: ChartPoint[] }) {
  if (points.length < 2) return null;

  const values = points.map((point) => toChartNumber(point.value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  // Плоский ряд (все нули у нового пользователя) дал бы деление на ноль;
  // рисуем его прямой по низу, а не прячем карточку.
  const span = max - min || 1;

  const step = 100 / (values.length - 1);
  const coords = values.map((value, index) => ({
    x: index * step,
    y: 56 - ((value - min) / span) * 52,
  }));

  const line = coords.map((point) => `${String(point.x)} ${String(point.y)}`).join(" L ");

  return (
    <svg viewBox="0 0 100 60" preserveAspectRatio="none" className="size-full">
      <g stroke="currentColor" strokeOpacity="0.22" strokeWidth="0.5">
        {coords.slice(1, -1).map((point) => (
          <path key={point.x} d={`M${String(point.x)} ${String(point.y)}V60`} />
        ))}
      </g>
      <path d={`M ${line}`} fill="none" stroke="currentColor" strokeWidth="1" />
      <g fill="var(--color-background-base)" stroke="currentColor" strokeWidth="1">
        {coords.filter((_, index) => index % 2 === 1).map((point) => (
          <circle key={point.x} cx={point.x} cy={point.y} r="2" />
        ))}
      </g>
    </svg>
  );
}
